import React from 'react';

/**
 * A small inline label: an organisation name, a role, an account state.
 *
 * Decision: ADR-057 — colour carries QC status and nothing else.
 * See docs/decisions/ADR-057-colour-means-status.md
 *
 * These used to be pill-shaped and individually coloured — teal for the
 * organisation, purple for Admin, green for Active, red for Inactive — hand-
 * rolled the same way on four admin pages. None of them is a QC verdict: an
 * organisation is a name, a role is something you hold, and an account being
 * switched off is a deliberate administrative state, not an error. Spending
 * green and red on them competes with the pass/fail colours that mean
 * something on the QC screens, and a red "Inactive" reads as a fault report.
 *
 * So a chip is neutral. Where two chips must be told apart, the icon and the
 * word do it — `muted` only recedes, it does not carry the meaning on its own
 * (accessibility.md › Vision: "Convey information with more than color
 * alone").
 *
 * For an actual QC verdict use JobStatusBadge, which owns the five tones.
 */
export type ChipTone = 'default' | 'muted';

interface ChipProps {
  children: React.ReactNode;
  /** `muted` recedes — for the less-usual of a pair, e.g. Inactive. */
  tone?: ChipTone;
  /** Decorative; it never carries meaning by itself. */
  icon?: React.ReactNode;
  'data-testid'?: string;
}

const TONE: Record<ChipTone, string> = {
  default: 'border-border bg-bg-subtle text-text-secondary',
  muted: 'border-border text-text-tertiary',
};

export const Chip: React.FC<ChipProps> = ({
  children,
  tone = 'default',
  icon,
  'data-testid': testId,
}) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${TONE[tone]}`}
  >
    {icon}
    {children}
  </span>
);
